/**
 * Pharmacy settings.
 *
 * Thresholds and safeguards, the pharmacist register reviews depend on, job
 * presets for staff, storage areas and shelves, and the growth features —
 * which are shown, honestly, as off until what they depend on exists. There
 * is no switch for those here: a switch that "turns on" delivery with no
 * delivery partner connected would simulate a service that is not there.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, WifiOff } from 'lucide-react';
import {
  PHARMACY_JOB_LABELS,
  PHARMACY_JOB_PERMISSIONS,
  PHARMACY_JOB_PRESETS,
  type Member,
  type PharmacyJobPreset,
} from '@cenox/shared';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { api } from '../../lib/api';
import { money } from '../../lib/format';
import { useWorkingLocation } from '../../lib/locationContext';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Select, Spinner, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type ListOf, type LoyaltySummary, type PharmacySettings } from './data';
import { ErrorLine, Table, Td } from './parts';

export function PharmacySettingsPage(): ReactNode {
  const { can } = useAuth();
  return (
    <div className="space-y-5">
      <PageHeader title="Pharmacy settings" description="Safeguards, pharmacists, staff roles, storage and what is switched on" />
      <SettingsForm editable={can('pharmacy:settings')} />
      <RestrictedSales editable={can('pharmacy:settings')} />
      <LoyaltyPoints editable={can('pharmacy:settings')} />
      <GrowthFeatures />
      {can('members:update') && <Pharmacists />}
      {can('members:update') && <JobPresets />}
      <StorageAreas editable={can('pharmacy:settings')} />
      <Shelves editable={can('pharmacy:catalogue')} />
      <OutageProcedure />
    </div>
  );
}

// ── Safeguards ─────────────────────────────────────────────────────────────

function SettingsForm({ editable }: { editable: boolean }): ReactNode {
  const settings = usePharmacy<PharmacySettings>('/settings');
  const save = usePharmacyWrite<Record<string, unknown>>('patch', '/settings');
  const [form, setForm] = useState({
    expiryAlertDays: '90',
    sellCutoffDays: '0',
    maxDiscountPercent: '10',
    enforcePrescriptions: true,
    requireRegisteredReviewer: true,
    restrictedRequiresWitness: false,
  });
  const s = settings.data;
  useEffect(() => {
    if (!s) return;
    setForm({
      expiryAlertDays: String(s.expiryAlertDays),
      sellCutoffDays: String(s.sellCutoffDays),
      maxDiscountPercent: s.maxDiscountPercent,
      enforcePrescriptions: s.enforcePrescriptions,
      requireRegisteredReviewer: s.requireRegisteredReviewer,
      restrictedRequiresWitness: s.restrictedRequiresWitness,
    });
  }, [s]);

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate({
      expiryAlertDays: Number(form.expiryAlertDays),
      sellCutoffDays: Number(form.sellCutoffDays),
      maxDiscountPercent: form.maxDiscountPercent,
      enforcePrescriptions: form.enforcePrescriptions,
      requireRegisteredReviewer: form.requireRegisteredReviewer,
      restrictedRequiresWitness: form.restrictedRequiresWitness,
    });
  }

  if (settings.isError) return <ErrorState message="Could not load settings." onRetry={() => void settings.refetch()} />;
  if (!s) return <Spinner />;

  return (
    <Card>
      <CardHeader title="Safeguards" subtitle={editable ? 'Applied by the server on every sale and review' : 'Read only — you cannot change these'} />
      <form onSubmit={submit} className="space-y-4">
        <fieldset disabled={!editable} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Expiry alert (days ahead)" type="number" min={0} max={1095} value={form.expiryAlertDays} onChange={(e) => setForm({ ...form, expiryAlertDays: e.target.value })} hint="Batches expiring within this are flagged" />
          <Input label="Stop selling (days before expiry)" type="number" min={0} max={365} value={form.sellCutoffDays} onChange={(e) => setForm({ ...form, sellCutoffDays: e.target.value })} hint="0 sells until the expiry date" />
          <Input label="Largest discount (%)" inputMode="decimal" value={form.maxDiscountPercent} onChange={(e) => setForm({ ...form, maxDiscountPercent: e.target.value })} hint="The owner is not limited" />
        </fieldset>
        <fieldset disabled={!editable} className="space-y-2">
          <Toggle checked={form.enforcePrescriptions} onChange={(v) => setForm({ ...form, enforcePrescriptions: v })} label="Prescription-only medicines need an approved prescription" hint="Restricted medicines always do, whatever this says." />
          <Toggle checked={form.requireRegisteredReviewer} onChange={(v) => setForm({ ...form, requireRegisteredReviewer: v })} label="Only pharmacists with a registration on file may review prescriptions" />
          <Toggle checked={form.restrictedRequiresWitness} onChange={(v) => setForm({ ...form, restrictedRequiresWitness: v })} label="Restricted dispensing needs a named witness" />
        </fieldset>
        <ErrorLine error={save.error} />
        {editable && (
          <div className="flex items-center gap-3">
            <Button type="submit" loading={save.isPending}>Save</Button>
            {save.isSuccess && <span className="text-sm text-emerald-700">Saved</span>}
          </div>
        )}
      </form>
    </Card>
  );
}

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }): ReactNode {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
      <input type="checkbox" className="mt-0.5 size-4 rounded border-slate-300" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block text-sm font-medium text-slate-900">{label}</span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

// ── Growth features ────────────────────────────────────────────────────────

const GROWTH: Array<{ key: string; label: string; flag: keyof PharmacySettings }> = [
  { key: 'onlineOrders', label: 'Online orders and pickup', flag: 'onlineOrdersEnabled' },
  { key: 'delivery', label: 'Ceno Hub delivery', flag: 'deliveryEnabled' },
  { key: 'refillReminders', label: 'Refill reminders (consent-based)', flag: 'refillRemindersEnabled' },
  { key: 'companyBilling', label: 'HMO / company billing', flag: 'companyBillingEnabled' },
  { key: 'cardIntegration', label: 'Integrated card and transfer payments', flag: 'cardIntegrationEnabled' },
];

function GrowthFeatures(): ReactNode {
  const settings = usePharmacy<PharmacySettings>('/settings');
  const s = settings.data;
  if (!s) return null;
  return (
    <Card>
      <CardHeader
        title="Growth features"
        subtitle="Each stays disabled until what it depends on is configured and verified. Nothing is simulated."
      />
      <ul className="divide-y divide-slate-100">
        {GROWTH.map((g) => {
          const on = Boolean(s[g.flag]);
          return (
            <li key={g.key} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{g.label}</p>
                <p className="text-sm text-slate-500">{s.dependencies[g.key]}</p>
              </div>
              <Badge tone={on ? 'success' : 'neutral'}>{on ? 'On' : 'Off — dependency missing'}</Badge>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        Card and transfer payments can still be taken today: they are recorded as pending until a cashier confirms the
        terminal slip or bank alert, and only then count as paid.
      </p>
    </Card>
  );
}

// ── Pharmacists ────────────────────────────────────────────────────────────

interface PharmacistRow {
  id: string;
  user_id: string;
  full_name: string;
  registration_number: string;
  registration_expires_on: string | null;
}

function useMembers() {
  const { business } = useAuth();
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['members', business?.businessId],
    queryFn: () => api.get<{ data: Member[] }>(path('/members')),
    enabled: Boolean(business),
  });
}

function Pharmacists(): ReactNode {
  const list = usePharmacy<ListOf<PharmacistRow>>('/pharmacists');
  const members = useMembers();
  const add = usePharmacyWrite<{ userId: string; registrationNumber: string; registrationExpiresOn: string | null }>('post', '/pharmacists');
  const [userId, setUserId] = useState('');
  const [reg, setReg] = useState('');
  const [expires, setExpires] = useState('');

  const people = (members.data?.data ?? []).filter((m) => m.userId && m.status === 'active' && m.role !== 'location_operator');

  function submit(e: FormEvent) {
    e.preventDefault();
    add.mutate(
      { userId, registrationNumber: reg.trim(), registrationExpiresOn: expires || null },
      { onSuccess: () => { setUserId(''); setReg(''); setExpires(''); } },
    );
  }

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader
          title="Pharmacists"
          subtitle="Registrations on file. A review records the reviewer’s registration; an expired one cannot review."
        />
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-end">
          <Select label="Member" required value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Choose…" options={people.map((m) => ({ value: m.userId!, label: m.fullName ?? m.email }))} />
          <Input label="Registration number" required minLength={2} value={reg} onChange={(e) => setReg(e.target.value)} />
          <Input label="Expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          <Button type="submit" loading={add.isPending} disabled={!userId || reg.trim().length < 2}>Record</Button>
        </form>
        <div className="mt-3"><ErrorLine error={add.error} /></div>
      </div>
      {list.data && list.data.data.length > 0 ? (
        <Table head={['Pharmacist', 'Registration', 'Expires']} minWidth={420}>
          {list.data.data.map((p) => (
            <tr key={p.id}>
              <Td>{p.full_name}</Td>
              <Td className="font-mono">{p.registration_number}</Td>
              <Td>{p.registration_expires_on ?? '—'}</Td>
            </tr>
          ))}
        </Table>
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">No pharmacist registration recorded yet.</p>
      )}
    </Card>
  );
}

// ── Job presets ────────────────────────────────────────────────────────────

function presetOf(member: Member): PharmacyJobPreset | null {
  const grants = new Set(member.permissions?.grant ?? []);
  for (const preset of PHARMACY_JOB_PRESETS) {
    const p = PHARMACY_JOB_PERMISSIONS[preset];
    if (p.grant.every((g) => grants.has(g)) && grants.size === p.grant.length) return preset;
  }
  return null;
}

function JobPresets(): ReactNode {
  const members = useMembers();
  const path = useBusinessPath();
  const client = useQueryClient();
  const apply = useMutation({
    mutationFn: ({ id, preset }: { id: string; preset: PharmacyJobPreset }) => {
      const p = PHARMACY_JOB_PERMISSIONS[preset];
      return api.patch(path(`/members/${id}`), { permissions: { grant: [...p.grant], revoke: [...p.revoke] } });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['members'] }),
  });
  const [choice, setChoice] = useState<Record<string, PharmacyJobPreset>>({});

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader
          title="Staff roles"
          subtitle="Pharmacist, cashier and stockkeeper are presets applied to a staff member’s own login."
        />
        <p className="text-sm text-slate-500">
          Owners and admins already hold every pharmacy permission and need no preset. Each person keeps an individual
          account, so every sale, review and dispensing names them. Which locations someone may work in is set under{' '}
          <Link to="/locations" className="text-brand-700 hover:underline">Locations</Link> and the Team settings.
        </p>
        <div className="mt-3"><ErrorLine error={apply.error} /></div>
      </div>
      {members.isError ? (
        <div className="px-5 pb-5"><ErrorState message="Could not load the team." onRetry={() => void members.refetch()} /></div>
      ) : !members.data ? (
        <div className="px-5 pb-5"><Spinner /></div>
      ) : (
        <Table head={['Member', 'Role', 'Pharmacy preset', '']} minWidth={560}>
          {members.data.data
            .filter((m) => m.role !== 'location_operator' && m.role !== 'integration')
            .map((m) => {
              const current = presetOf(m);
              return (
                <tr key={m.id}>
                  <Td>
                    <p className="font-medium text-slate-900">{m.fullName ?? m.email}</p>
                    <p className="text-xs text-slate-500">{m.email}</p>
                  </Td>
                  <Td className="capitalize">{m.role}</Td>
                  <Td>{m.role === 'staff' ? (current ? <Badge tone="info">{PHARMACY_JOB_LABELS[current]}</Badge> : <span className="text-slate-400">None</span>) : <span className="text-slate-400">Not needed</span>}</Td>
                  <Td>
                    {m.role === 'staff' && (
                      <div className="flex items-center gap-2">
                        <select
                          aria-label={`Preset for ${m.fullName ?? m.email}`}
                          className="input-base h-8 w-36 py-0 text-sm"
                          value={choice[m.id] ?? current ?? 'cashier'}
                          onChange={(e) => setChoice({ ...choice, [m.id]: e.target.value as PharmacyJobPreset })}
                        >
                          {PHARMACY_JOB_PRESETS.map((p) => (
                            <option key={p} value={p}>{PHARMACY_JOB_LABELS[p]}</option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={apply.isPending && apply.variables?.id === m.id}
                          onClick={() => apply.mutate({ id: m.id, preset: choice[m.id] ?? current ?? 'cashier' })}
                        >
                          Apply
                        </Button>
                      </div>
                    )}
                  </Td>
                </tr>
              );
            })}
        </Table>
      )}
    </Card>
  );
}

// ── Storage areas and shelves ──────────────────────────────────────────────

interface AreaRow {
  id: string;
  branch_name: string;
  name: string;
  kind: string;
  min_celsius: string;
  max_celsius: string;
  open_excursion: boolean;
}

function StorageAreas({ editable }: { editable: boolean }): ReactNode {
  const { locations } = useWorkingLocation();
  const areas = usePharmacy<ListOf<AreaRow>>('/storage-areas');
  const add = usePharmacyWrite<Record<string, unknown>>('post', '/storage-areas');
  const [form, setForm] = useState({ branchId: '', name: '', kind: 'refrigerator', min: '2', max: '8' });

  function submit(e: FormEvent) {
    e.preventDefault();
    add.mutate(
      { branchId: form.branchId, name: form.name.trim(), kind: form.kind, minCelsius: Number(form.min), maxCelsius: Number(form.max) },
      { onSuccess: () => setForm({ ...form, name: '' }) },
    );
  }

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Storage areas" subtitle="Fridges, cool rooms and shelves with an acceptable temperature range" />
        {editable && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3 sm:grid-cols-6 sm:items-end">
            <div className="col-span-2"><Select label="Location" required value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value })} placeholder="Choose…" options={locations.map((l) => ({ value: l.id, label: l.name }))} /></div>
            <div className="col-span-2 sm:col-span-1"><Input label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <Select label="Kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} options={[{ value: 'room', label: 'Room' }, { value: 'cool', label: 'Cool' }, { value: 'refrigerator', label: 'Fridge' }, { value: 'freezer', label: 'Freezer' }]} />
            <Input label="Min °C" inputMode="decimal" value={form.min} onChange={(e) => setForm({ ...form, min: e.target.value })} />
            <Input label="Max °C" inputMode="decimal" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} />
            <div className="col-span-2 sm:col-span-6"><Button type="submit" loading={add.isPending} disabled={!form.branchId || !form.name.trim()}>Add area</Button></div>
          </form>
        )}
        <div className="mt-3"><ErrorLine error={add.error} /></div>
      </div>
      {areas.data && areas.data.data.length > 0 ? (
        <Table head={['Location', 'Area', 'Kind', 'Range', '']} minWidth={520}>
          {areas.data.data.map((a) => (
            <tr key={a.id}>
              <Td>{a.branch_name}</Td>
              <Td>{a.name}</Td>
              <Td className="capitalize">{a.kind}</Td>
              <Td className="tabular-nums">{a.min_celsius}–{a.max_celsius} °C</Td>
              <Td>{a.open_excursion && <Badge tone="danger">Open excursion</Badge>}</Td>
            </tr>
          ))}
        </Table>
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">No storage areas yet.</p>
      )}
    </Card>
  );
}

interface ShelfRow {
  id: string;
  branch_id: string;
  code: string;
  name: string | null;
  storage_area_name: string | null;
}

function Shelves({ editable }: { editable: boolean }): ReactNode {
  const { locations } = useWorkingLocation();
  const shelves = usePharmacy<ListOf<ShelfRow>>('/shelves');
  const areas = usePharmacy<ListOf<AreaRow & { branch_id: string }>>('/storage-areas');
  const add = usePharmacyWrite<Record<string, unknown>>('post', '/shelves');
  const [form, setForm] = useState({ branchId: '', code: '', name: '', storageAreaId: '' });
  const nameOf = new Map(locations.map((l) => [l.id, l.name]));

  function submit(e: FormEvent) {
    e.preventDefault();
    add.mutate(
      { branchId: form.branchId, code: form.code.trim(), name: form.name.trim() || null, storageAreaId: form.storageAreaId || null },
      { onSuccess: () => setForm({ ...form, code: '', name: '' }) },
    );
  }

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Shelves and bins" subtitle="Where things sit inside a location" />
        {editable && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:items-end">
            <div className="col-span-2 sm:col-span-1"><Select label="Location" required value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value, storageAreaId: '' })} placeholder="Choose…" options={locations.map((l) => ({ value: l.id, label: l.name }))} /></div>
            <Input label="Code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="A-03" />
            <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Select label="Storage area" value={form.storageAreaId} onChange={(e) => setForm({ ...form, storageAreaId: e.target.value })} placeholder="None" options={(areas.data?.data ?? []).filter((a) => !form.branchId || a.branch_id === form.branchId).map((a) => ({ value: a.id, label: a.name }))} />
            <Button type="submit" loading={add.isPending} disabled={!form.branchId || !form.code.trim()}>Add shelf</Button>
          </form>
        )}
        <div className="mt-3"><ErrorLine error={add.error} /></div>
      </div>
      {shelves.data && shelves.data.data.length > 0 ? (
        <Table head={['Location', 'Code', 'Name', 'Storage area']} minWidth={480}>
          {shelves.data.data.map((s) => (
            <tr key={s.id}>
              <Td>{nameOf.get(s.branch_id) ?? '—'}</Td>
              <Td className="font-mono">{s.code}</Td>
              <Td>{s.name ?? '—'}</Td>
              <Td>{s.storage_area_name ?? '—'}</Td>
            </tr>
          ))}
        </Table>
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">No shelves yet.</p>
      )}
    </Card>
  );
}

// ── Outage procedure ───────────────────────────────────────────────────────

function OutageProcedure(): ReactNode {
  const steps = [
    'Stop recording in CenoX. The till refuses to check out offline; nothing is queued silently.',
    'Use the paper outage log at the counter: time, items, quantities, amount, payment method, staff name.',
    'Cash only. A card or transfer is not accepted as paid until it can be verified afterwards.',
    'No restricted-medicine dispensing without a pharmacist present and a paper register entry.',
    'Prescriptions are dispensed only by a pharmacist, recorded on paper with the prescription kept.',
    'When the connection returns, a manager back-enters the log, noting the original times, then runs the reconciliation checks under Reports.',
  ];
  return (
    <Card className="border-amber-200">
      <CardHeader title="If the internet goes down" subtitle="The agreed outage procedure for this pharmacy" />
      <div className="flex gap-3">
        <WifiOff className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
        <ol className="list-decimal space-y-1.5 pl-4 text-sm text-slate-700">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <p className={cn('mt-4 flex items-center gap-2 text-xs text-slate-500')}>
        <AlertTriangle className="size-3.5" aria-hidden /> Full procedure: docs/PHARMACY.md
      </p>
    </Card>
  );
}

// ── Restricted medicines ───────────────────────────────────────────────────

/**
 * Restricted medicines are unsellable until the owner records the pharmacy's
 * regulatory sign-off. Classifying an item restricted describes it; this is
 * what licenses selling it. Switching it off again needs only a reason.
 */
function RestrictedSales({ editable }: { editable: boolean }): ReactNode {
  const { business } = useAuth();
  const settings = usePharmacy<PharmacySettings>('/settings');
  const save = usePharmacyWrite<Record<string, unknown>>('post', '/settings/restricted-sales');
  const [text, setText] = useState('');
  const s = settings.data;
  if (!s) return null;
  const isOwner = business?.role === 'owner';
  return (
    <Card>
      <CardHeader
        title="Restricted medicines"
        subtitle="Controlled medicines can be classified and stocked, but not sold, until the regulatory sign-off is recorded."
      />
      <div className="mt-3 space-y-3 text-sm">
        {s.restrictedSalesEnabled ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
            Selling allowed since {s.restrictedSalesEnabledAt ? new Date(s.restrictedSalesEnabledAt).toLocaleDateString() : '—'} — sign-off:{' '}
            {s.restrictedSalesSignOff}
          </p>
        ) : (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
            Selling is OFF. The till refuses every restricted medicine.
          </p>
        )}
        {editable && (s.restrictedSalesEnabled || isOwner) && (
          <>
            <Input
              label={s.restrictedSalesEnabled ? 'Reason for switching it off' : 'Regulatory sign-off reference'}
              hint={s.restrictedSalesEnabled ? 'Recorded in the activity log.' : 'Who signed, which document, and its date. Owner only.'}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <ErrorLine error={save.error} />
            <Button
              variant={s.restrictedSalesEnabled ? 'secondary' : 'primary'}
              loading={save.isPending}
              disabled={text.trim().length < 3}
              onClick={() =>
                save.mutate(
                  s.restrictedSalesEnabled ? { enabled: false, reason: text.trim() } : { enabled: true, signOff: text.trim() },
                  { onSuccess: () => setText('') },
                )
              }
            >
              {s.restrictedSalesEnabled ? 'Stop selling restricted medicines' : 'Allow selling restricted medicines'}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

// ── Loyalty points ─────────────────────────────────────────────────────────

/**
 * Loyalty points (0159). Off until the owner switches them on; the owner sets
 * the rates. Anybody who manages pharmacy settings may switch them off.
 * Points are earned only on paid non-medicines and spent only on them.
 */
function LoyaltyPoints({ editable }: { editable: boolean }): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const settings = usePharmacy<PharmacySettings>('/settings');
  const summary = usePharmacy<LoyaltySummary>('/loyalty/summary', {}, editable);
  const save = usePharmacyWrite<Record<string, unknown>>('post', '/settings/loyalty');
  const s = settings.data;
  const [form, setForm] = useState({ earningEnabled: false, redemptionEnabled: false, spendPerPoint: '100.00', pointValue: '1.00', reason: '' });
  useEffect(() => {
    if (s) {
      setForm((f) => ({
        ...f,
        earningEnabled: s.loyaltyEarningEnabled,
        redemptionEnabled: s.loyaltyRedemptionEnabled,
        spendPerPoint: s.loyaltySpendPerPoint,
        pointValue: s.loyaltyPointValue,
      }));
    }
  }, [s]);
  if (!s) return null;
  const isOwner = business?.role === 'owner';
  const on = s.loyaltyEarningEnabled || s.loyaltyRedemptionEnabled;
  return (
    <Card>
      <CardHeader
        title="Loyalty points"
        subtitle="Customers on file earn points on paid non-medicines and spend them at the till on non-medicines. Never on medicines."
      />
      <div className="mt-3 space-y-3 text-sm">
        <p className={cn('rounded-lg border px-3 py-2', on ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700')}>
          Earning is {s.loyaltyEarningEnabled ? 'ON' : 'OFF'} · redeeming is {s.loyaltyRedemptionEnabled ? 'ON' : 'OFF'} · 1 point per{' '}
          {money(s.loyaltySpendPerPoint, currency)} of non-medicines paid for · 1 point is worth {money(s.loyaltyPointValue, currency)}
        </p>
        {summary.data && (summary.data.pointsEarned > 0 || summary.data.pointsRedeemed > 0) && (
          <p className="text-slate-600">
            {summary.data.pointsOutstanding} points held by {summary.data.customersWithPoints} customers, worth{' '}
            {money(summary.data.outstandingValue, currency)} at today’s value · {money(summary.data.discountGiven, currency)} given as points
            discounts so far
            {summary.data.customersBelowZero > 0 && ` · ${summary.data.customersBelowZero} below zero after refunds`}
          </p>
        )}
        {editable && (
          <>
            {!isOwner && <p className="text-xs text-slate-500">Only the owner can switch points on or change the rates. You can switch them off.</p>}
            <Toggle
              checked={form.earningEnabled}
              onChange={(v) => setForm((f) => ({ ...f, earningEnabled: v }))}
              label="Customers earn points"
              hint="On a sale to a customer on file, once it is paid in full. Points on what is refunded are taken back."
            />
            <Toggle
              checked={form.redemptionEnabled}
              onChange={(v) => setForm((f) => ({ ...f, redemptionEnabled: v }))}
              label="Customers can spend points at the till"
              hint="As a discount on non-medicines only, never more than they hold."
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Spend for 1 point"
                inputMode="decimal"
                value={form.spendPerPoint}
                disabled={!isOwner}
                onChange={(e) => setForm((f) => ({ ...f, spendPerPoint: e.target.value }))}
              />
              <Input
                label="Value of 1 point"
                inputMode="decimal"
                value={form.pointValue}
                disabled={!isOwner}
                onChange={(e) => setForm((f) => ({ ...f, pointValue: e.target.value }))}
              />
            </div>
            <Input
              label="Reason"
              hint="Recorded in the activity log with the change."
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
            <ErrorLine error={save.error} />
            <Button
              loading={save.isPending}
              disabled={form.reason.trim().length < 3}
              onClick={() => save.mutate({ ...form, reason: form.reason.trim() }, { onSuccess: () => setForm((f) => ({ ...f, reason: '' })) })}
            >
              Save loyalty settings
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
